"use client";

import { useEffect, useRef, useState } from "react";
import { Volume2Icon, VolumeXIcon } from "lucide-react";
import clsx from "clsx";

export interface VideoPlayerProps {
  src: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  hasAudio?: boolean;
  onEnded?: () => void;
  shouldPlay?: boolean;
  className?: string;
  rounded?: boolean;
}

export function VideoPlayer({
  src,
  autoPlay = false,
  loop = false,
  muted: initialMuted = true,
  hasAudio = false,
  onEnded,
  shouldPlay,
  className,
  rounded = true,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(initialMuted);

  useEffect(() => {
    if (!videoRef.current) return;
    if (shouldPlay) {
      videoRef.current.play().catch(() => {});
    } else if (shouldPlay === false) {
      videoRef.current.pause();
    }
  }, [shouldPlay]);

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVideoEnded = () => {
    if (onEnded) {
      onEnded();
    }
  };

  return (
    <div className={clsx("tw-relative tw-w-full tw-h-full", className)}>
      <video
        ref={videoRef}
        src={src}
        className={clsx("tw-w-full tw-h-full tw-object-cover", rounded && "tw-rounded-2xl")}
        autoPlay={autoPlay}
        loop={loop}
        muted={isMuted}
        playsInline
        onEnded={handleVideoEnded}
      />

      {hasAudio && (
        <button
          onClick={handleMuteToggle}
          className='tw-absolute tw-top-2 tw-right-2 tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-shadow-sm hover:tw-scale-105 tw-transition-transform tw-duration-300 bg-default'
          aria-label={isMuted ? "Unmute" : "Mute"}
        >
          {!isMuted ? (
            <Volume2Icon size={16} className='text-default' fill='none' />
          ) : (
            <VolumeXIcon size={16} className='text-default' fill='currentColor' />
          )}
        </button>
      )}
    </div>
  );
}
